import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, RotateCcw, Archive } from "lucide-react";

const apiErr = (e) => e?.response?.data?.detail || e?.message || "Something went wrong";
const fmtDate = (s) => (s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

export default function HistoryPage() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);

  const load = (query = "") => {
    setLoading(true);
    api.get(`/leads/history${query ? `?q=${encodeURIComponent(query)}` : ""}`)
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(() => load(q), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const restore = async (row) => {
    if (!window.confirm(`Restore ${row.order_number} back to the active pipeline?`)) return;
    setRestoring(row.id);
    try {
      await api.post(`/leads/history/${row.id}/restore`);
      toast.success(`${row.order_number} restored to pipeline`);
      setRows((rs) => rs.filter((x) => x.id !== row.id));
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setRestoring(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="history-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-primary font-bold mb-1">Archive</div>
        <h1 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight uppercase flex items-center gap-2">
          <Archive className="h-6 w-6 text-primary" /> Deleted Leads
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Read-only history of removed leads. Restore any record back to the active pipeline.</p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search order no, name, phone…"
          className="rounded-none bg-card pl-9 h-10"
          data-testid="history-search"
        />
      </div>

      {/* Desktop table */}
      <div className="hidden md:block border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border bg-muted">
            <tr>
              <th className="px-4 py-2.5 text-left">Order</th>
              <th className="px-4 text-left">Customer</th>
              <th className="px-4 text-left">Route</th>
              <th className="px-4 text-left">Deleted</th>
              <th className="px-4 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="p-10 text-center text-muted-foreground">Loading history…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-muted-foreground">No deleted leads found.</td></tr>}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors" data-testid={`history-row-${r.order_number}`}>
                <td className="px-4 py-2.5">
                  <div className="font-mono-num text-primary">{r.order_number}</div>
                  <div className="text-[11px] text-muted-foreground uppercase">{r.category}</div>
                </td>
                <td className="px-4">
                  <div className="font-medium">{r.customer_name || "—"}</div>
                  <div className="font-mono-num text-xs text-muted-foreground">{r.customer_phone}</div>
                </td>
                <td className="px-4 text-muted-foreground">{r.route}</td>
                <td className="px-4">
                  <div className="text-xs">{fmtDate(r.deleted_at)}</div>
                  <div className="text-[11px] text-muted-foreground">by {r.deleted_by || "—"}</div>
                </td>
                <td className="px-4 text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-none h-8 text-xs font-bold uppercase"
                    onClick={() => restore(r)}
                    disabled={restoring === r.id}
                    data-testid={`history-restore-${r.order_number}`}
                  >
                    <RotateCcw className="h-3.5 w-3.5 mr-1" /> {restoring === r.id ? "Restoring…" : "Restore"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {loading && <div className="p-10 text-center text-muted-foreground">Loading history…</div>}
        {!loading && rows.length === 0 && <div className="p-10 text-center text-muted-foreground">No deleted leads found.</div>}
        {!loading && rows.map((r) => (
          <div key={r.id} className="border border-border bg-card p-3" data-testid={`history-card-${r.order_number}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-mono-num text-primary text-sm font-bold">{r.order_number}</div>
                <div className="text-[11px] text-muted-foreground uppercase">{r.category}</div>
              </div>
              <Button variant="outline" size="sm" className="rounded-none h-8 text-xs font-bold uppercase" onClick={() => restore(r)} disabled={restoring === r.id} data-testid={`history-card-restore-${r.order_number}`}>
                <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore
              </Button>
            </div>
            <div className="mt-2 text-xs space-y-1">
              <div><span className="text-muted-foreground">Customer:</span> {r.customer_name || "—"} · <span className="font-mono-num">{r.customer_phone}</span></div>
              <div><span className="text-muted-foreground">Route:</span> {r.route}</div>
              <div><span className="text-muted-foreground">Deleted:</span> {fmtDate(r.deleted_at)} by {r.deleted_by || "—"}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
